import { useCallback, useEffect, useMemo, useState } from "react";
import { Check, Search, Save } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import HA from "../../lib/locales/ha.json";
import IG from "../../lib/locales/ig.json";
import YO from "../../lib/locales/yo.json";
import PCM from "../../lib/locales/pcm.json";
import { HA as HA_MANUAL } from "../../lib/ha";

const BASE = { ha: { ...HA, ...HA_MANUAL }, ig: IG, yo: YO, pcm: PCM };
const NAMES = { ha: "Hausa", ig: "Igbo", yo: "Yorùbá", pcm: "Nigerian Pidgin" };

export default function AdminTranslations() {
  const [lang, setLang] = useState("ha");
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [overrides, setOverrides] = useState({});
  const [progress, setProgress] = useState({});
  const [drafts, setDrafts] = useState({});
  const [page, setPage] = useState(0);

  const load = useCallback(() => {
    api.get(`/admin/translations?lang=${lang}`).then((r) => setOverrides(r.data.overrides)).catch(() => {});
    api.get("/admin/translations/progress").then((r) => setProgress(r.data)).catch(() => {});
  }, [lang]);
  useEffect(() => { load(); setDrafts({}); setPage(0); }, [load]);

  const keys = useMemo(() => Object.keys(BASE[lang]).sort((a, b) => a.localeCompare(b)), [lang]);
  const rows = useMemo(() => keys.filter((k) => {
    const o = overrides[k];
    if (filter === "reviewed" && !o?.reviewed) return false;
    if (filter === "unreviewed" && o?.reviewed) return false;
    if (filter === "edited" && !o?.value) return false;
    if (!q) return true;
    const s = q.toLowerCase();
    return k.toLowerCase().includes(s) || (o?.value || BASE[lang][k] || "").toLowerCase().includes(s);
  }), [keys, overrides, filter, q, lang]);
  const PAGE = 40;
  const visible = rows.slice(page * PAGE, page * PAGE + PAGE);
  const total = keys.length;
  const reviewed = progress[lang]?.reviewed || 0;

  const save = async (k, reviewed) => {
    const value = drafts[k] ?? overrides[k]?.value ?? BASE[lang][k];
    try {
      const { data } = await api.put(`/admin/translations/${lang}`, { key: k, value, reviewed });
      setOverrides({ ...overrides, [k]: data });
      setDrafts((d) => { const n = { ...d }; delete n[k]; return n; });
      toast.success(reviewed ? "Saved & marked reviewed" : "Saved");
      api.get("/admin/translations/progress").then((r) => setProgress(r.data)).catch(() => {});
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <div data-testid="admin-translations">
      <div className="mb-6"><h1 className="font-serif text-3xl font-bold">Translation review</h1><p className="text-[#0a0d12]/60 text-sm">Correct any machine-translated string in place. Your edits go live immediately and override the machine version. Tick “Reviewed” once a native speaker has confirmed a string.</p></div>
      <div className="flex flex-wrap gap-2 mb-4" data-testid="translation-langs">
        {Object.keys(NAMES).map((l) => {
          const p = progress[l] || {}; const pct = total ? Math.round(((p.reviewed || 0) / Object.keys(BASE[l]).length) * 100) : 0;
          return (
            <button key={l} onClick={() => setLang(l)} className={`px-4 py-2 rounded-xl border text-left min-w-[150px] ${lang === l ? "bg-[#155e3f] text-white border-[#0d1724]" : "bg-white border-black/10"}`} data-testid={`translation-lang-${l}`}>
              <div className="font-bold text-sm">{NAMES[l]}</div>
              <div className="text-[10px] opacity-70">{p.reviewed || 0}/{Object.keys(BASE[l]).length} reviewed · {p.edited || 0} edited</div>
              <div className="h-1.5 rounded bg-black/10 mt-1 overflow-hidden"><div className="h-full bg-[#d6aa43]" style={{ width: `${pct}%` }} /></div>
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[240px]"><Search size={14} className="absolute left-3 top-3 text-[#0a0d12]/40" /><input value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} placeholder="Search English or translation…" className="w-full pl-9 pr-3 py-2.5 border border-black/15 rounded-md text-sm" data-testid="translation-search" /></div>
        <select value={filter} onChange={(e) => { setFilter(e.target.value); setPage(0); }} className="px-3 py-2.5 border border-black/15 rounded-md text-sm bg-white" data-testid="translation-filter"><option value="all">All strings</option><option value="unreviewed">Not yet reviewed</option><option value="reviewed">Reviewed</option><option value="edited">Edited by staff</option></select>
        <div className="text-xs text-[#0a0d12]/60" data-testid="translation-count">{rows.length} of {total} · {reviewed} reviewed</div>
      </div>
      <div className="bg-white rounded-xl border border-black/5 divide-y divide-black/5">
        {visible.map((k) => {
          const o = overrides[k]; const current = drafts[k] ?? o?.value ?? BASE[lang][k]; const dirty = drafts[k] !== undefined && drafts[k] !== (o?.value ?? BASE[lang][k]);
          return (
            <div key={k} className="p-4 grid md:grid-cols-[1fr_1fr_auto] gap-3 items-start" data-testid="translation-row">
              <div className="text-sm text-[#0a0d12]/80"><div className="text-[10px] uppercase tracking-widest text-[#0a0d12]/40 font-bold mb-1">English</div>{k}</div>
              <div><div className="text-[10px] uppercase tracking-widest text-[#0a0d12]/40 font-bold mb-1">{NAMES[lang]}{o?.value && o.value !== BASE[lang][k] ? " · edited" : " · machine"}</div>
                <textarea value={current} onChange={(e) => setDrafts({ ...drafts, [k]: e.target.value })} rows={Math.min(4, Math.ceil((current || "").length / 60) || 1)} className={`w-full px-3 py-2 border rounded-md text-sm ${dirty ? "border-[#d6aa43]" : "border-black/15"}`} data-testid="translation-input" /></div>
              <div className="flex md:flex-col gap-2 md:items-end">
                <button onClick={() => save(k, o?.reviewed || false)} className="text-xs px-3 py-1.5 rounded-md bg-[#155e3f] text-white inline-flex items-center gap-1" data-testid="translation-save"><Save size={12} /> Save</button>
                <button onClick={() => save(k, !o?.reviewed)} className={`text-xs px-3 py-1.5 rounded-md inline-flex items-center gap-1 border ${o?.reviewed ? "bg-green-100 text-green-800 border-green-200" : "border-black/15"}`} data-testid="translation-reviewed"><Check size={12} /> {o?.reviewed ? "Reviewed" : "Mark reviewed"}</button>
                {o?.reviewed && <div className="text-[10px] text-[#0a0d12]/50 text-right">{o.reviewed_by}<br />{o.reviewed_at?.slice(0, 10)}</div>}
              </div>
            </div>
          );
        })}
        {!visible.length && <div className="p-8 text-center text-sm text-[#0a0d12]/50">No strings match.</div>}
      </div>
      {rows.length > PAGE && <div className="flex items-center justify-between mt-4 text-sm"><button disabled={page === 0} onClick={() => setPage(page - 1)} className="dk-btn-outline !py-1.5 !px-3 !text-[#0d1724] !border-[#0d1724] disabled:opacity-40" data-testid="translation-prev">Previous</button><span>Page {page + 1} of {Math.ceil(rows.length / PAGE)}</span><button disabled={(page + 1) * PAGE >= rows.length} onClick={() => setPage(page + 1)} className="dk-btn-outline !py-1.5 !px-3 !text-[#0d1724] !border-[#0d1724] disabled:opacity-40" data-testid="translation-next">Next</button></div>}
    </div>
  );
}
